import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useContext } from 'react';
import {
  Route,
  Routes as ReactRoutes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';
import { useAnalytics } from 'use-analytics';

import FooterContainer from 'components/footer/FooterContainer';
import Banner from 'components/signUpFunnel/Banner';
import SkipContainer from 'components/signUpFunnel/InternalSkip/SkipContainer';
import BillingContainer from 'components/signUpFunnel/pickMeals/BillingContainer';
import MenuContainer from 'components/signUpFunnel/pickMeals/menu/MenuContainer';
import PickYourMealsSlideOut from 'components/signUpFunnel/pickMeals/PickYourMealsSlideOut';
import PickPlanContainer from 'components/signUpFunnel/pickPlan/PickPlanContainer';
import { HOME_CHEF } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useScrollToTopOnRouteChange from 'hooks/useScrollToTopOnRouteChange';
import { FAMILY, TEMPO, POPULATE_DATA_LAYER, ENABLE_INTERNAL_SKIP_FUNNEL } from 'lib/constants';

import BackgroundContainer from './BackgroundContainer';
import BoxContainer from './box/BoxContainer';
import BoxRecommenderContainer from './boxRecommender/BoxRecommenderContainer';
import CheckoutContainer from './checkout/CheckoutContainer';
import {
  UserContextProvider,
  FunnelContextProvider,
  BasketContextProvider,
  VoucherContextProvider,
  PickYourMealsContextProvider,
  PickYourMealsContext,
} from './Contexts';
import FunnelUserWrapper from './FunnelUserWrapper';
import IntroContainer from './intro/IntroContainer';
import NavBar from './pickMeals/NavBar';
import PreferencesAvoidContainer from './preferences/PreferencesAvoidContainer';
import PreferencesDietaryContainer from './preferences/PreferencesDietaryContainer';
import ProgressBar from './ProgressBar';
import PromoContainer from './promo/PromoContainer';
import ThankYouContainer from './thankYou/ThankYouContainer';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(ReactRoutes);

Sentry.addIntegration(
  Sentry.reactRouterV6BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  })
);

const updateMainContentStyles = () => {
  const mainContent = document.getElementById('mainContent');
  const siteWrapper = document.getElementsByClassName('siteWrapper')[0];
  if (mainContent) {
    mainContent.style.maxWidth = 'none';
    mainContent.style.height = '100%';
    siteWrapper.style.height = '1px';
  }
};

const PickYourMealsRoutes = () => {
  const tempoRoutes = Routes.getBrandRoute({ brand: TEMPO });

  return (
    <SentryRoutes>
      <Route path={Routes.PICK_MEALS_MENU_PATH} element={<MenuContainer />} />
      <Route path={tempoRoutes.pickMealsBilling} element={<BillingContainer />} />
      <Route path={tempoRoutes.pickMealsThankYou} element={<ThankYouContainer />} />
    </SentryRoutes>
  );
};

const FunnelRoutes = () => {
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;

  const homeChefRoutes = Routes.getBrandRoute({
    brand: HOME_CHEF,
    isFamily: isFamilyMealPlan,
    addSuffix: false,
  });

  return (
    <SentryRoutes>
      {isFamilyMealPlan && (
        <Route path={`${homeChefRoutes.pickPlan}/:mealPlanSlug?`} element={<PickPlanContainer />} />
      )}
      <Route path={`${homeChefRoutes.intro}/:mealPlanSlug?`} element={<IntroContainer />} />
      <Route
        path={`${homeChefRoutes.preferenceDietary}/:mealPlanSlug?`}
        element={<PreferencesDietaryContainer />}
      />
      <Route
        path={`${homeChefRoutes.preferenceAvoid}/:mealPlanSlug?`}
        element={<PreferencesAvoidContainer />}
      />
      <Route path={`${homeChefRoutes.promo}/:mealPlanSlug?`} element={<PromoContainer />} />
      <Route
        path={`${homeChefRoutes.boxRecommenderPeople}/:mealPlanSlug?`}
        element={<BoxRecommenderContainer />}
      />
      <Route
        path={`${homeChefRoutes.boxRecommenderMeals}/:mealPlanSlug?`}
        element={<BoxRecommenderContainer />}
      />
      <Route
        path={`${homeChefRoutes.boxRecommenderDays}/:mealPlanSlug?`}
        element={<BoxRecommenderContainer />}
      />
      <Route path={`${homeChefRoutes.box}/:mealPlanSlug?`} element={<BoxContainer />} />
      <Route path={`${homeChefRoutes.billing}/:mealPlanSlug?`} element={<CheckoutContainer />} />
      <Route path={`${homeChefRoutes.thankyou}/:mealPlanSlug?`} element={<ThankYouContainer />} />
    </SentryRoutes>
  );
};

const App = () => {
  const analytics = useAnalytics();
  const mealPlanSlug = useFunnelMealPlan();
  const isInternal = useFeatureFlag(ENABLE_INTERNAL_SKIP_FUNNEL);
  const location = useLocation();

  useScrollToTopOnRouteChange();

  useEffect(() => {
    updateMainContentStyles();
  }, []);

  useEffect(() => {
    analytics.track(POPULATE_DATA_LAYER, { mealPlanSlug });
  }, [analytics, mealPlanSlug]);

  const isSignUpPath = location.pathname.startsWith('/signup');

  const { isPickYourMeals, isPickYourMealsSlideOut } = useContext(PickYourMealsContext);

  return (
    <>
      {isPickYourMeals || isPickYourMealsSlideOut ? (
        <FunnelUserWrapper>
          {isInternal && <SkipContainer mealPlan={mealPlanSlug} />}
          <NavBar />
          <PickYourMealsRoutes />
        </FunnelUserWrapper>
      ) : (
        <>
          <ProgressBar />
          <FunnelUserWrapper>
            <BackgroundContainer>
              <FunnelRoutes />
            </BackgroundContainer>
          </FunnelUserWrapper>
          <FooterContainer />
          {isInternal && isSignUpPath && !location.pathname.includes(Routes.INTRO_PATH) && (
            <SkipContainer mealPlan={mealPlanSlug} />
          )}
        </>
      )}
    </>
  );
};

const FunnelContainer = () => (
  <VoucherContextProvider>
    <Banner />
    <UserContextProvider>
      <FunnelContextProvider>
        <ReactQueryDevtools initialIsOpen={false} />
        <BasketContextProvider>
          <PickYourMealsContextProvider>
            <App />
            <PickYourMealsSlideOut />
          </PickYourMealsContextProvider>
        </BasketContextProvider>
      </FunnelContextProvider>
    </UserContextProvider>
  </VoucherContextProvider>
);

export default FunnelContainer;
